import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Clock, Trash2, Loader2, Users, Search, Bell, BellOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWaitlistSection = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/waitlist`, { headers: getAuthHeaders() });
      setEntries(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/waitlist/${id}`, { headers: getAuthHeaders() });
      toast.success('Rimosso dalla lista');
      fetchEntries();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const filtered = entries.filter(e =>
    !searchTerm ||
    e.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.client_phone?.includes(searchTerm)
  );

  const pendingEntries = filtered.filter(e => !e.notified);
  const notifiedEntries = filtered.filter(e => e.notified);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="waitlist-section">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-1">Lista d'Attesa</h2>
          <p className="text-muted-foreground text-sm">
            Clienti in attesa di un posto libero. Vengono notificati automaticamente quando si libera un posto.
          </p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 bg-secondary px-3 py-1.5 rounded-lg">
            <Clock className="h-4 w-4 text-accent" />
            <span className="text-sm font-semibold">{pendingEntries.length} in attesa</span>
          </div>
          <div className="flex items-center gap-2 bg-secondary px-3 py-1.5 rounded-lg">
            <Bell className="h-4 w-4 text-green-400" />
            <span className="text-sm font-semibold">{notifiedEntries.length} notificati</span>
          </div>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca per nome o telefono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="waitlist-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">Nessuno in lista d'attesa</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((entry) => (
            <Card key={entry.id} className="card-elegant" data-testid={`waitlist-${entry.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-full ${entry.notified ? 'bg-green-500/10' : 'bg-accent/10'}`}>
                      {entry.notified ? <Bell className="h-4 w-4 text-green-400" /> : <Clock className="h-4 w-4 text-accent" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{entry.client_name}</h3>
                        <Badge className={entry.notified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-500'}>
                          {entry.notified ? 'Notificato' : 'In Attesa'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{entry.client_phone}</p>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
                        <span>Data: <strong>{entry.preferred_date}</strong></span>
                        <span>Servizio: <strong>{entry.preferred_service}</strong></span>
                        {entry.preferred_time && <span>Orario: <strong>{entry.preferred_time}</strong></span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Iscritto: {format(new Date(entry.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                      </p>
                    </div>
                  </div>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(entry.id)} data-testid={`delete-waitlist-${entry.id}`}>
                    <Trash2 className="h-4 w-4 mr-1" /> Rimuovi
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminWaitlistSection;
